import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { getCurrentUser } from '../../../services/authService';
import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { AutScanDataItem, CustomAutSummary, CustomUTMOverview, EquipmentAnomalySummaryItem, FVIEquipmentItem, GeneralInformationData, NewAnomalyItem, PDFImageProps, PIDData, UTMInspection, User } from '../../../utils/models';
import { getAnomalyStartNumber, getSomeChecklistItemsMissing, matchPhrases, reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';
import { EQUIPMENT_ANOMALY, EQUIPMENT_CHECKLIST } from '../custom-visual-inspection/createCustomFVIEquipmentReport';

import AUTGeneralInformationForm from '../../forms/aut/general-information/autGeneralInformationForm';
import AutPhotographicRecordForm from '../../forms/aut/autPhotographicRecord';
import AutScanItem from './aut-scan-item';
import AUTScanDataItemNewForm from '../../forms/custom-aut/scan-data/autScanDataItemNewForm';
import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomUTMSection from '../custom-utm/custom-utm-section';
import CustomAutGeneralDrawingForm from '../../forms/custom-aut/autGeneralDrawing';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomExxonMobilAutReport from './';
import CustomAutSummaryForm from '../../forms/custom-aut/autSummaryForm';
import CustomNewItem from '../custom-general/custom-new-item';
import Elevation from '../../themed/instruction-modals/elevation';
import EquipmentSummaryForm from '../../forms/custom-fvi/equipmentSummaryForm';
import EquipmentOverview from '../custom-visual-inspection/equipment-overview';
import FVICoverData from '../../themed/fvi-cover-data';
import FVIStructurePIDImageForm from '../../forms/custom-fvi/fviStructurePIDImageForm';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import PECScanPlanForm from '../../forms/pec/scan-and-plan-view/pecScanPlanForm';
import SyncTime from '../../blocks/sync-time';
import Zippy from '../../themed/zippy';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import WarningNotice from '../custom-visual-inspection/warning-notice';

import useAnomalyRecords from '../../../hooks/useAnomalyRecords';
import useAutoSaveReport from '../../../hooks/useAutoSave';
import useEquipmentAnomalySummaryUpdate from '../../../hooks/useEquipmentAnomalySummaryUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useMappedAnomalies from '../../../hooks/useMappedAnomalies';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

export type EquipmentSummaryItem = Pick<FVIEquipmentItem, 'equipmentNumber' | 'additionalComment' | 'checklistItems' | 'anomalySummary' | 'hasAnomalies' | 'anomalyItems'>;

const CreateCustomExxonMobileAutReport: React.FC = () => {
	const { projectDetails, error } = useProjectDetails();

	const { activeTutorial } = useAppSelector(getTutorials);
	const { isSaving } = useAppSelector(getReportDetails);
	const { fviCoverData } = useAppSelector(getCoverData);

	const [equipmentSummary, setEquipmentSummary] = useState(initialEquipmentSummary);
	const [previewReport, setPreviewReport] = useState(false);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [summaryData, setSummaryData] = useState(initialSummaryData);
	const [generalDrawing, setGeneralDrawing] = useState<PDFImageProps[]>([]);
	const [generalInfo, setGeneralInfo] = useState(initialGeneralInformation);
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
	const [autScanData, setAUTScanData] = useState<AutScanDataItem[]>([]);
	const [scanPlan, setScanPlan] = useState<PDFImageProps[]>([]);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [utInspectionOverview, setUTInspectionOverview] = useState(initialUTMOverview);
	const [utInspectionItems, setUTInspectionItems] = useState<UTMInspection[]>([]);

	const reportDetails = useMemo(() => {
		return {
			autScanData,
			coverData,
			photoRecord,
			pidData,
			projectDetails,
			generalDrawing,
			generalInfo,
			scanPlan,
			summaryData,
			reportTitle,
			equipmentSummary,
			utInspectionItems,
			utInspectionOverview,
		};
	}, [autScanData, coverData, equipmentSummary, photoRecord, pidData, projectDetails, generalDrawing, generalInfo, scanPlan, summaryData, reportTitle, utInspectionItems, utInspectionOverview]);

	const mappedAnomalies = useMappedAnomalies([{ id: 1, anomalyItems: equipmentSummary.anomalyItems }]);
	
	const validationMessage = useMemo(() => {
		const { coverData, equipmentSummary, pidData, generalInfo } = reportDetails;

		if (!validateWorkOrderNumber(coverData.workOrderNumber)) {
            return 'Enter a valid work order number!';
        }

        if (matchPhrases(pidData.hasPID, 'yes') && pidData.pIDNumber.length === 0) {
            return 'Please provide at least one P&ID image!';
        }

		const missingChecklist = getSomeChecklistItemsMissing(equipmentSummary.checklistItems, mappedAnomalies);
		
		if (missingChecklist) {
			return missingChecklist;
		}

		if (!generalInfo.objectInformation?.objectSize || matchPhrases(generalInfo.objectInformation.objectSize?.toString(), 'n/a')) {
			return 'Please provide an accurate equipment size';
		}

		if (!generalInfo.autEquipmentInformation?.dateOfInspection) {
			return 'Please select a valid date of inspection';
		}

		if (!generalInfo.autEquipmentInformation?.autEquipmentBrand) {
			return 'AUT Equipment Brand must be provided!';
		}

		if (!equipmentSummary) {
			return 'Please provide equipment information!'
		}

		if (!equipmentSummary.equipmentNumber) {
			return 'Please provide a valid vessel number';
		}

		if (matchPhrases(equipmentSummary.hasAnomalies, 'yes') && equipmentSummary.anomalyItems.length === 0) {
			return 'Please provide anomaly information';
		}

		return '';
	}, [reportDetails, mappedAnomalies]);

	const isFVIIncomplete = useMemo(() => {
		const { anomalySummary, checklistItems, equipmentNumber } = reportDetails.equipmentSummary; 
		
		const isChecklistMissing = getSomeChecklistItemsMissing(checklistItems, mappedAnomalies);
		const isSummaryIncomplete = Object.keys(anomalySummary).some((key) => _.get(anomalySummary, key) === '');

		return isChecklistMissing || isSummaryIncomplete || equipmentNumber.length === 0;
	}, [reportDetails.equipmentSummary, mappedAnomalies]);

	const user = getCurrentUser() as User;
	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const handleAddScanItem = useCallback((newScanItem: AutScanDataItem) => {
		const scanMatch = autScanData.find((scan) => matchPhrases(scan.zoneName, newScanItem.zoneName));
		if (scanMatch) return toastService.success('Zone already added!');

		setAUTScanData((previous) => [...previous, newScanItem]);
	}, [autScanData]);

	const handleEditScanItem = useCallback((originalItem: AutScanDataItem, updatedItem: AutScanDataItem) => {
		const updatedItems = autScanData.map((scan) => scan.zoneName === originalItem.zoneName ? updatedItem : scan);
		setAUTScanData(updatedItems);
	}, [autScanData]);

	const handleDeleteScanItem = useCallback((itemToDelete: AutScanDataItem) => {
		const items = autScanData.filter((scan) => scan.zoneName !== itemToDelete.zoneName);
		setAUTScanData(items);
	}, [autScanData]);

	const handleSaveEquipmentSummary = useCallback((summary: Pick<FVIEquipmentItem, 'hasAnomalies' | 'equipmentNumber'>) => {
		setEquipmentSummary((previous) => ({
			...previous,
			hasAnomalies: summary.hasAnomalies,
			equipmentNumber: summary.equipmentNumber,
		}));
	}, []);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}
		
		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

	useEffect(() => {
		function initCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

				return;
			}

			if (cachedReport.equipmentSummary) {
				setEquipmentSummary(cachedReport.equipmentSummary);
			}

			setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
			setPhotoRecord(reArrangePhotoRecord(cachedReport.photoRecord));
			setGeneralDrawing(cachedReport.generalDrawing);
			setPIDData(cachedReport.pidData);
			setAUTScanData(cachedReport.autScanData);
			setGeneralInfo(cachedReport.generalInfo);
			setReportTitle(cachedReport.reportTitle);
			setScanPlan(cachedReport.scanPlan);
			setSummaryData(cachedReport.summaryData);
			setUTInspectionOverview(cachedReport.utInspectionOverview);
			setUTInspectionItems(cachedReport.utInspectionItems);
			
			dispatch(setLastPhotoNumber(cachedReport.photoRecord.length));
		}

		initCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.UTM));
	}, [dispatch]);

	useAnomalyRecords(equipmentSummary.anomalyItems);
	useInspectedItemIDUpdate([equipmentSummary.equipmentNumber]);

	useEquipmentAnomalySummaryUpdate({
		isNewFormat: true,
		anomalyItems: equipmentSummary.anomalyItems, 
		anomalySummary: equipmentSummary.anomalySummary,
		onSaveAnomalySummary: (anomalySummary) => setEquipmentSummary((previous) => ({ ...previous, anomalySummary })),
	});

	return (
		<>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomExxonMobilAutReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilAUT[projectDetails.organisationId]}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.exxonMobilAUT && user.role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} AUT Reports`}
				/>
			)}

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice
					title='Report Information Incomplete' 
					body={validationMessage} 
				/>
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>

			<Zippy headerTitle="Summary Sheet" isValid={summaryData.executiveSummary.length > 0}>
				<CustomAutSummaryForm initialSummaryData={summaryData} onAdd={setSummaryData} />
			</Zippy>

			<Zippy headerTitle="General Information" isValid={!!generalInfo.objectInformation.objectSize}>
				<AUTGeneralInformationForm initialData={generalInfo} enableObjectInformation enableMaxwellData={false} onProceed={setGeneralInfo} />
			</Zippy>

			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

			<Conditional isVisible={config.enableFVIReportEmbedding}>
				<Zippy headerTitle="FVI Anomaly Details" isValid={!isFVIIncomplete}>
					<EquipmentSummaryForm
						initialData={_.pick(equipmentSummary, ['equipmentNumber', 'hasAnomalies'])}
						onSave={handleSaveEquipmentSummary}
					/>

					<Conditional isVisible={equipmentSummary.equipmentNumber.length > 0}>
						<div className="p-2 rounded border bg-light mt-4">
							<EquipmentOverview
								equipmentId={1}
								pdoChecklistItems={[]}
								onChangePDOChecklistItems={() => {}}
								anomalyItems={equipmentSummary.anomalyItems}
								equipmentNumber={equipmentSummary.equipmentNumber}
								additionalComment={equipmentSummary.additionalComment}
								anomalySummary={equipmentSummary.anomalySummary}
								checklistItems={equipmentSummary.checklistItems}
								hasAnomalies={equipmentSummary.hasAnomalies}
								nextAnomalyID={getAnomalyStartNumber([equipmentSummary])}
								isNewFormat
								isPDOFormat={false}
								isChecklistVisible
								onChangeAnomalies={(anomalyItems: NewAnomalyItem[]) => setEquipmentSummary((previos) => ({ ...previos, anomalyItems }))}
								onChangeSummary={(anomalySummary: EquipmentAnomalySummaryItem) => setEquipmentSummary((previous) => ({ ...previous, anomalySummary }))}
								onChangeAdditionalComment={(additionalComment) => setEquipmentSummary((previous) => ({ ...previous, additionalComment }))}
								onChangeChecklistItems={(checklistItems) => setEquipmentSummary((previous) => ({ ...previous, checklistItems }))}
							/>
						</div>
					</Conditional>
				</Zippy>
			</Conditional>

			<Zippy headerTitle="General Drawing" isValid={generalDrawing.length > 0}>
				<CustomAutGeneralDrawingForm initialGeneralDrawing={generalDrawing} onSave={setGeneralDrawing} />
			</Zippy>

			<Zippy headerTitle="Scan Plan" isValid={scanPlan.length > 0}>
				<PECScanPlanForm initialImages={scanPlan} onProceed={setScanPlan} />
			</Zippy>

			<Zippy headerTitle="Scan Data" isValid={autScanData.length > 0}>
				<CustomNewItem label='Add New Scan Section'>
					<AUTScanDataItemNewForm onProceed={handleAddScanItem} />
				</CustomNewItem>

				{autScanData.map((scan, index) => (
					<AutScanItem
						onDeleteScanItem={handleDeleteScanItem}
						onEditScanItem={(updatedItem) => handleEditScanItem(scan, updatedItem)}
						isOpenOnDefault={index === (autScanData.length - 1)}
						key={scan.zoneName}
						initialData={scan} 
					/>
				))}
			</Zippy>

			<CustomUTMSection
				tmlSectionLabel="Scan Plan"
				tmlLocations={scanPlan}
				onChangeTMLLocations={setScanPlan}
				utmItems={utInspectionItems}
				utmOverview={utInspectionOverview}
				onItemsChange={setUTInspectionItems}
				onOverviewChange={setUTInspectionOverview}
			/>

			<Zippy headerTitle="General Photos" isValid={photoRecord.length > 0}>
				<AutPhotographicRecordForm initialImages={photoRecord} onProceed={setPhotoRecord} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</>
	);
};

const initialSummaryData: Omit<CustomAutSummary, 'summaryItems'> = {
	executiveSummary: '',
	conclusion: '',
	recommendation: '',
};

const initialGeneralInformation: GeneralInformationData = {
	objectInformation: {
		objectSize: 'N/A',
		acceptanceCode: 'ASME Section V Ed 2021 / API 510',
		testedWelds: 'As painted',
		manufactureYear: '',
		manufacturingCode: 'API 510',
		material: 'Carbon Steel',
		couplantUsed: 'Water',
		nominalThickness: 0,
		calibrationBlock: '12.5mm V2 BlocK',
	},
	systemSettings: {
		prf: '2',
		eht: '240',
		scanMode: 'Bi-directional',
		averagingSample: '',
		gain: 'variable',
		pulseWidth: 250,
		digitizationRate: 50,
		lowPassFilter: 2,
		highPassFilter: 0,
		probeAngle: 0,
		collectionMode: 'Encoded',
		adDelay: 'variable',
		adWidth: 'variable',
	},
	autEquipmentInformation: {
		autSolution: 'Accuscan',
		autSolutionSN: '19/007',
		autEquipmentBrand: '',
		inspectionProbes: '5Mhz',
		inspectionProbesSN: 'GJB905',
		dateOfInspection: new Date(),
		calibrationDueDate: new Date(),
	},
};

const initialUTMOverview: Omit<CustomUTMOverview, 'data'> = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
	techniqueType: '',
	velocity: 0,
};

const initialEquipmentSummary: EquipmentSummaryItem = {
	additionalComment: '',
	anomalyItems: [],
	anomalySummary: EQUIPMENT_ANOMALY,
	checklistItems: EQUIPMENT_CHECKLIST,
	equipmentNumber: '',
	hasAnomalies: 'yes',
};

export default CreateCustomExxonMobileAutReport;
